/**
 * Composant de formulaire d'inscription
 * Permet aux utilisateurs de créer un compte avec un pseudo et un mot de passe
 */
import { useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import '../styles/auth.css';

/**
 * SignupForm - Composant de formulaire d'inscription
 * @returns {JSX.Element} Formulaire d'inscription avec gestion d'erreurs
 */
export default function SignupForm() {
    // Hook d'authentification fournissant les fonctions d'inscription et les états
    const { register, loading, error } = useAuth();
    // Hook de navigation pour rediriger après inscription réussie
    const navigate = useNavigate();
    // État local pour le pseudo de l'utilisateur
    const [pseudo, setPseudo] = useState('');
    // État local pour le mot de passe de l'utilisateur
    const [password, setPassword] = useState('');
    // État local pour la confirmation du mot de passe
    const [confirmPassword, setConfirmPassword] = useState('');
    // État local pour les erreurs de validation
    const [validationError, setValidationError] = useState('');

    /**
     * Gère la soumission du formulaire d'inscription
     * @param {Event} e - L'événement de soumission du formulaire
     */
    const handleSubmit = async (e) => {
        // Empêche le rechargement de la page lors de la soumission
        e.preventDefault();
        // Réinitialise les erreurs de validation
        setValidationError('');

        // Validation: vérifier que les mots de passe correspondent
        if (password !== confirmPassword) {
            setValidationError('Les mots de passe ne correspondent pas');
            return;
        }

        // Validation: vérifier la longueur minimale du mot de passe
        if (password.length < 6) {
            setValidationError('Le mot de passe doit contenir au moins 6 caractères');
            return;
        }

        try {
            // Tente d'inscrire l'utilisateur avec les identifiants fournis
            await register(pseudo, password);
            // Redirige vers la page principale en cas de succès
            navigate('/main');
        } catch (err) {
            // Affiche l'erreur dans la console (l'erreur visuelle est gérée par le hook useAuth)
            console.error('Erreur d\'inscription:', err);
        }
    };

    return (
        <div className="login-container">
            <div className="login-form-wrapper">
                {/* Logo */}
                <div className="logo">
                    <img src="/logo.png" alt="Culture Quiz Logo" />
                </div>

                {/* Titre */}
                <h1 className="login-title">S'INSCRIRE</h1>

                {/* Description */}
                <p className="login-description">
                    Vous devez être connecté pour jouer aux quiz. <br />
                    Votre compte permet de sauvegarder votre progression et de jouer sur plusieurs appareils.
                </p>

                {/* Formulaire d'inscription */}
                <form className="login-form" onSubmit={handleSubmit}>
                    <div className="form-group">
                        <input
                            className="form-input"
                            type="text"
                            placeholder="Pseudo"
                            value={pseudo}
                            onChange={(e) => setPseudo(e.target.value)}
                            required
                        />
                    </div>

                    <div className="form-group">
                        <input
                            className="form-input"
                            type="password"
                            placeholder="Mot de passe"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                        />
                    </div>

                    <div className="form-group">
                        <input
                            className="form-input"
                            type="password"
                            placeholder="Confirmer mot de passe"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            required
                        />
                    </div>

                    <button type="submit" className="submit-button" disabled={loading}>
                        {loading ? 'Inscription...' : 'Valider'}
                    </button>

                    {(error || validationError) && (
                        <p className="error-message">{error || validationError}</p>
                    )}
                </form>

                {/* Lien vers la connexion */}
                <div className="auth-links">
                    <div className="auth-link-group">
                        <h3>J'ai déjà un compte ?</h3>
                        <p>Vous pouvez vous connecter en cliquant <a href="/login" className="auth-link">ICI</a>.</p>
                    </div>
                </div>

                {/* Footer avec liens légaux */}
                <div className="footer-links">
                    <a href="/privacy" className="footer-link">Politique de confidentialité</a>
                    <a href="/terms" className="footer-link">CGU</a>
                </div>
            </div>
        </div>
    );
}
